'use client';
import { useLocalStorage } from '#pplx/frontend/browser-storage/hooks/useLocalStorage.js';
import { useRequestLocation } from '#pplx/frontend/locations/internal/hooks/useRequestLocation.js';
import { getCurrentPosition, listenForPermissionChange, queryGeolocationPermission, } from '#pplx/frontend/locations/internal/utils/geolocation.js';
import { useCallback, useEffect, useMemo } from 'react';
const LOCATION_CACHE_EXPIRATION_TIME = 1000 * 60 * 30; // 30 minutes
export const useDeviceLocation = ({ origin = 'unknown', onPermissionSuccess, onPermissionError, onLocationRequested, trackEvent, } = {}) => {
    const [locationData, setLocationData] = useLocalStorage('locationMetadata', undefined);
    const handleSetLocation = useCallback((location, permissionState) => {
        setLocationData({ location, permissionState, updatedAt: Date.now() });
    }, [setLocationData]);
    const handlePermissionSuccess = useCallback((position) => {
        onPermissionSuccess?.(position);
        handleSetLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
        }, 'granted');
    }, [handleSetLocation, onPermissionSuccess]);
    const handlePermissionError = useCallback((error) => {
        onPermissionError?.(error);
        handleSetLocation(undefined, 'denied');
    }, [handleSetLocation, onPermissionError]);
    const handleRequestLocation = useRequestLocation({
        onSuccess: handlePermissionSuccess,
        onError: handlePermissionError,
        origin,
        onRequest: onLocationRequested,
        trackEvent,
    });
    const isExpired = Date.now() - (locationData?.updatedAt ?? 0) > LOCATION_CACHE_EXPIRATION_TIME;
    useEffect(() => {
        if (!navigator.geolocation || !('permissions' in navigator))
            return;
        let permissionStatus = null;
        let removePermissionChangeListener;
        let isMounted = true;
        const fetchLocation = () => {
            getCurrentPosition((position) => {
                if (!isMounted)
                    return;
                handleSetLocation({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                }, 'granted');
            }, () => {
                if (!isMounted)
                    return;
                handleSetLocation(undefined, 'denied');
            });
        };
        const handlePermissionChange = () => {
            if (!isMounted)
                return;
            if (permissionStatus?.state === 'granted') {
                fetchLocation();
            }
            else {
                handleSetLocation(undefined, permissionStatus?.state);
            }
        };
        const permissionQuery = queryGeolocationPermission();
        if (!permissionQuery)
            return;
        permissionQuery
            .then((status) => {
            if (!isMounted)
                return;
            permissionStatus = status;
            // Only fetch on initial load when cache is expired
            if (isExpired) {
                if (status.state === 'granted') {
                    fetchLocation();
                }
                else {
                    handleSetLocation(undefined, status.state);
                }
            }
            removePermissionChangeListener = listenForPermissionChange(status, handlePermissionChange);
        })
            .catch(() => { });
        return () => {
            isMounted = false;
            removePermissionChangeListener?.();
        };
    }, [handleSetLocation, isExpired]);
    return useMemo(() => ({
        location: locationData?.location,
        permissionState: locationData?.permissionState,
        isPreciseLocationEnabled: locationData?.permissionState === 'granted' &&
            !isNaN(locationData?.location?.latitude ?? 0) &&
            !isNaN(locationData?.location?.longitude ?? 0),
        requestLocation: handleRequestLocation,
    }), [locationData?.location, locationData?.permissionState, handleRequestLocation]);
};
//# sourceMappingURL=useDeviceLocation.js.map